import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Stack,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import {
  KURUL_SEKRETER_TIPLERI,
  KURUL_UYE_TIPI_ETIKETI,
  kurulSiklikTavaniAy,
} from '@isg/validation';
import type { KurulSekreterTipi } from '@isg/validation';
import { useKurulKur, useKurulSecenekleri } from '../api/queries';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * KURUL KURMA — AYRI SAYFA (P1; Kapsam §28/C1 · C4). Sıklık tavanı sınıftan:
 * ekran seçenekleri TAVANA göre çizer (`kurulSiklikTavaniAy` — sunucunun
 * 422 kapısıyla AYNI sabit; §2.40: seçilemeyecek değer sunulmaz).
 */
export function KurulYeniScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const secenekler = useKurulSecenekleri();
  const kur = useKurulKur();
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [siklikAy, setSiklikAy] = useState<number>(1);
  const [siklikKararNotu, setSiklikKararNotu] = useState('');
  const [sekreter, setSekreter] = useState<KurulSekreterTipi>('isg_uzmani');
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [kurulus, setKurulus] = useVarsayilanGun();
  const isyeri = (secenekler.data?.isyerleri ?? []).find((w) => w.id === workplaceId) ?? null;
  const tavan = isyeri ? kurulSiklikTavaniAy(isyeri.tehlikeSinifi) : 3;
  const engel: string | null =
    workplaceId === null
      ? 'Önce işyerini seçin'
      : isyeri?.kurulVar
        ? 'Bu işyerinde zaten kurul var'
        : gunGirdisiIso(kurulus) === null
          ? 'Geçerli bir kuruluş tarihi girin'
          : siklikAy > 1 && !siklikKararNotu.trim()
            ? 'Sıklığı gevşetmek kurul kararı ister — kararın toplantısını yazın'
            : null;
  return (
    <Stack data-testid="kurul-yeni-sayfasi">
      <Link to="/isg-kurulu" data-testid="detay-geri">
        ← İSG kurulu
      </Link>
      <Title order={3}>Kurul kur</Title>
      <QueryStatus queries={[secenekler]} />
      {kur.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-yeni-hata">
          {kur.error.message}
        </Alert>
      )}
      <Card withBorder>
        <Stack>
          <Group align="flex-end" wrap="wrap">
            <Select
              w={360}
              label="İşyeri"
              data-testid="kurul-isyeri"
              searchable
              data={(secenekler.data?.isyerleri ?? []).map((w) => ({
                value: w.id,
                label: `${w.companyAd} — ${w.ad} (${w.calisanSayisi} çalışan${w.kurulVar ? ' · kurulu var' : ''})`,
              }))}
              value={workplaceId}
              onChange={(v) => {
                setWorkplaceId(v);
                setSiklikAy(1);
              }}
            />
            <NumberInput
              w={160}
              label={`Toplantı sıklığı (ay, en çok ${tavan})`}
              data-testid="kurul-siklik"
              min={1}
              max={tavan}
              value={siklikAy}
              onChange={(v) => setSiklikAy(typeof v === 'number' ? v : 1)}
            />
            <Select
              w={220}
              label="Sekreter"
              data-testid="kurul-sekreter"
              allowDeselect={false}
              value={sekreter}
              onChange={(v) => setSekreter((v as KurulSekreterTipi) ?? 'isg_uzmani')}
              data={KURUL_SEKRETER_TIPLERI.map((t) => ({
                value: t,
                label: KURUL_UYE_TIPI_ETIKETI[t],
              }))}
            />
            <TextInput
              w={180}
              type="date"
              label="Kuruluş tarihi"
              data-testid="kurul-kurulus"
              value={kurulus}
              onChange={(e) => setKurulus(e.currentTarget.value)}
            />
          </Group>
          {siklikAy > 1 && (
            <Textarea
              label="Gevşetme kararı (hangi toplantı)"
              data-testid="kurul-siklik-notu"
              value={siklikKararNotu}
              onChange={(e) => setSiklikKararNotu(e.currentTarget.value)}
            />
          )}
          <Group justify="flex-end" align="center">
            {engel !== null && (
              <span style={{ fontSize: 12, opacity: 0.7 }} data-testid="kurul-kaydet-engeli">
                {engel}
              </span>
            )}
            <Button
              data-testid="kurul-kaydet"
              disabled={engel !== null || kur.isPending}
              loading={kur.isPending}
              onClick={() => {
                if (!workplaceId) return;
                kur.mutate(
                  {
                    workplaceId,
                    siklikAy,
                    siklikKararNotu: siklikAy > 1 ? siklikKararNotu.trim() : null,
                    sekreterUyeTipi: sekreter,
                    kurulusTarihi: kurulus,
                  },
                  { onSuccess: (k) => k.id && void navigate(`/isg-kurulu/${k.id}`) },
                );
              }}
            >
              Kurulu kur
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
